import {Component, OnInit, Input, Output, EventEmitter} from '@angular/core';
import * as moment_ from 'moment';

const moment = moment_;

@Component({
  selector: 'calendar-viewer',
  templateUrl: './calendar-viewer.component.html',
  styleUrls: ['./calendar-viewer.component.scss']
})
export class CalendarViewerComponent implements OnInit {

  @Input() dateFormat: string = 'MMMM/YYYY';

  @Input() locale: string = 'vi';

  @Input() firstDay: number = 1;

  @Input() limitEvent: number = 3;

  @Input() view: CalendarViewerMode = CalendarViewerMode.MONTH;

  @Input() set date(date: Date) {
    let _date;
    if (!date || !date.getTime()) {
      _date = new Date();
    } else {
      _date = new Date(date);
    }

    _date.setHours(0, 0, 0, 0);

    if (this.view === CalendarViewerMode.MONTH) {
      // Set date start month
      _date.setDate(1);
    }

    // current date view
    if (this._date && _date.getTime() === this._date.getTime()) {
      return;
    }

    this._date = _date;

    this._render();
  }

  @Output() onChange = new EventEmitter<Date>();
  @Output() onActive = new EventEmitter<CalendarViewerDate>();


  dateTitle: string = '';

  dates: CalendarViewerDate[] = [];
  weekDates: CalendarViewerDate[][] = [];
  weekTitles: string[] = [];
  dateActive?: CalendarViewerDate;

  private _date?: Date;

  get date(): Date {
    return this._date ? new Date(this._date) : new Date();
  }

  constructor() {

  }

  ngOnInit() {
    if (!this._date) {
      this.date = new Date();
    }
  }

  setEvents(events: any, date: Date) {
    let dateViewer = this._getDateViewer(date);
    if (dateViewer) {
      dateViewer.events = events;
    }
  }

  clearEvents() {
    this.dates.forEach((dateViewer: any) => dateViewer.events = null);
  }

  toDay() {
    this.date = new Date();
  }

  prev() {
    if (this.view === CalendarViewerMode.MONTH) {
      this._prevMonth();
    } else {
      this._prevWeek();
    }
    this._render();
  }

  next() {
    if (this.view === CalendarViewerMode.MONTH) {
      this._nextMonth();
    } else {
      this._nextWeek();
    }
    this._render();
  }

  activeDate(dateViewer: CalendarViewerDate) {
    this.dateActive = dateViewer;

    // Move to target month
    if (dateViewer.outOfRange) {
      this.date = dateViewer.date;
    }

    this.onActive.emit(dateViewer);
  }

  focusDate(date: Date) {
    let dateViewer = this._getDateViewer(date);
    if (dateViewer) {
      this.activeDate(dateViewer);
    }
  }

  private _nextMonth() {
    if(this._date) {
      this._date.setMonth(this._date.getMonth() + 1);
    }
  }

  private _prevMonth() {
    if(this._date) {
      this._date.setMonth(this._date.getMonth() - 1);
    }
  }

  private _nextWeek() {
    if(this._date) {
      this._date.setDate(this._date.getDate() + 7);
    }
  }

  private _prevWeek() {
    if(this._date) {
      this._date.setDate(this._date.getDate() - 7);
    }
  }

  private _render() {
    if (this.view === CalendarViewerMode.MONTH) {
      this._renderMonth();
    } else {
      this._renderWeek();
    }

    this.onChange.emit(this.date);
  }

  private _renderMonth() {
    let date: any = this._getDateStartWeek();

    this.dates = [];
    this.weekDates = [];
    while (this.weekDates.length <= 6) {
      this.weekDates.push(this._createWeekDate(date));
      // detect date is end month
      if (this._date && date.getMonth() !== this._date.getMonth()) {
        break;
      }
    }

    this._renderTitle();
    this._renderWeekTitle(this.weekDates[0]);
  }

  private _renderWeek() {
    let date: any = this._getDateStartWeek();
    this.dates = [];
    this.weekDates = [this._createWeekDate(date)];
    this.weekDates[0].forEach(date => date.outOfRange = false);
    this._renderTitle();
    this._renderWeekTitle(this.weekDates[0]);
  }

  private _createWeekDate(date: Date): CalendarViewerDate[] {
    var weekDates: CalendarViewerDate[] = [];
    let dateViewer: CalendarViewerDate;
    for (let i = 0; i < 7; i++) {
      dateViewer = {
        date: new Date(date),
        time: date.getTime(),
        outOfRange: !!(this._date && this._date.getMonth() !== date.getMonth()),
        today: this._isToday(date)
      };
      weekDates.push(dateViewer);
      this.dates.push(dateViewer);
      date.setDate(date.getDate() + 1);
    }

    return weekDates;
  }

  private _renderWeekTitle(weekDates: CalendarViewerDate[]) {
    this.weekTitles = [];
    weekDates.forEach((viewerDate: CalendarViewerDate) => {
      this.weekTitles.push(moment(viewerDate.date).locale(this.locale).format('dd'));
    });
  }

  private _renderTitle() {
    this.dateTitle = moment(this._date).locale(this.locale).format(this.dateFormat);
  }

  private _getDateStartWeek(): Date | null {
    if (this._date) {
      let date = new Date(this._date);
      let startWeekFromNow = date.getDay() - this.firstDay;

      if (date.getDay() < this.firstDay) {
        startWeekFromNow += 7;
      }

      // Set date start week
      date.setDate(date.getDate() - startWeekFromNow);

      return date;
    }
    return null;
  }

  private _isToday(date: Date): boolean {
    var now = new Date();
    return now.getDate() === date.getDate() && now.getMonth() === date.getMonth() && now.getFullYear() === date.getFullYear();
  }

  private _getDateViewer(date: Date): any {
    let _date = new Date(date);
    _date.setHours(0, 0, 0, 0);
    for (let i = 0; i < this.dates.length; i++) {
      if (this.dates[i].time === _date.getTime()) {
        return this.dates[i];
      }
    }
  }
}

export interface CalendarViewerDate {
  date: Date;
  time: number;
  outOfRange: boolean;
  today: boolean;
  events?: any[];
}

export enum CalendarViewerMode {
  MONTH = 'month',
  WEEK = 'week'
}
